import { Box, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { useMaTransactionsContext } from '../../MaTransactionsContext';
import { TABLE_COLUMNS, TABLE_PAGE_SIZE_OPTIONS } from './constants';
import { useDownloadTransactions } from './hooks/useDownloadTransactions';
import { useGetTableRows } from './hooks/useGetTableRows';
import { useStyles } from './styles';

const SESSION_STORAGE_KEY = 'MaTransactionTableSettings';
const getInitialTableSettings = () => {
  const sessionSettings = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (sessionSettings) {
    const { sortModel, paginationModel, columnVisibilityModel } = JSON.parse(sessionSettings);
    return { sortModel, paginationModel, columnVisibilityModel };
  }

  return {
    sortModel: [{ field: 'effectiveDate', sort: 'desc' }],
    paginationModel: { page: 0, pageSize: TABLE_PAGE_SIZE_OPTIONS[1] },
    columnVisibilityModel: {},
  };
};

export const TransactionTable = () => {
  const classes = useStyles();

  const { setSnack } = useSnackBarContext();
  const { filters } = useMaTransactionsContext();
  const [downloading, setDownloading] = useState(false);
  const { downloadTransactions } = useDownloadTransactions();

  const {
    sortModel: defaultSortModel,
    paginationModel: defaultPaginationModel,
    columnVisibilityModel: defaultColumnVisibilityModel,
  } = getInitialTableSettings();
  const [sortModel, setSortModel] = useState(defaultSortModel);
  const [paginationModel, setPaginationModel] = useState(defaultPaginationModel);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(defaultColumnVisibilityModel);

  useEffect(() => {
    // Save the current state to sessionStorage
    sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ sortModel, paginationModel, columnVisibilityModel }),
    );
  }, [paginationModel, sortModel, columnVisibilityModel]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset page to 0 when the page size changes
  useEffect(() => {
    setPaginationModel(prev => ({
      ...prev,
      page: 0,
    }));
  }, [paginationModel.pageSize]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset page to 0 when the filters change
  useEffect(() => {
    setPaginationModel(prev => ({
      ...prev,
      page: 0,
    }));
  }, [filters]);

  const { rows, totalCount, loading, error, onRowClick } = useGetTableRows({
    paginationModel,
    sortModel,
  });

  // Memoize the rowCount to prevent the table from re-setting to 0 when the totalCount changes
  // Reference: https://mui.com/x/react-data-grid/pagination/
  const rowCountRef = useRef(totalCount || 0);
  const rowCount = useMemo(() => {
    if (totalCount !== undefined) {
      rowCountRef.current = totalCount;
    }
    return rowCountRef.current;
  }, [totalCount]);

  const getRowClassName = params => {
    if (params.row.isGroup) {
      return params.row.isExpanded ? classes.tableRowGroupExpanded : classes.tableRowGroup;
    }
    if (params.row.isTransaction && params.row.isFirst) {
      return classes.tableRowTransactionFirst;
    }
    if (params.row.isTransaction && params.row.isLast) {
      return classes.tableRowTransactionLast;
    }
    if (params.row.isTransaction) {
      return classes.tableRowTransaction;
    }
    if (params.row.isLoadMore) {
      return classes.tableRowLoadMore;
    }
    return '';
  };

  const onDownload = useCallback(async () => {
    setDownloading(true);
    setSnack({
      open: true,
      message: 'Your download should begin shortly.',
      severity: 'success',
    });

    const { ok, errors } = await downloadTransactions();

    if (!ok) {
      setSnack({
        open: true,
        message: errors[0]?.message || "We're sorry, but something went wrong.",
        severity: 'error',
      });
    }
    setDownloading(false);
  }, [downloadTransactions, setSnack]);

  if (error) return null;

  return (
    <Box>
      <Button
        className={classes.download}
        variant='contained'
        onClick={onDownload}
        disabled={downloading}
      >
        Download Data
      </Button>
      <Box className={classes.container}>
        <Box className={classes.tableWrapper}>
          <DataGrid
            disableRowSelectionOnClick
            className={classes.table}
            loading={loading}
            columns={TABLE_COLUMNS}
            rows={rows}
            rowCount={rowCount}
            getRowId={row => row.rowId}
            onRowClick={onRowClick}
            getRowClassName={getRowClassName}
            paginationMode='server'
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
            sortingMode='server'
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            slotProps={{
              // Add comma separator to numbers in the pagination label
              pagination: {
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from.toLocaleString()} - ${to.toLocaleString()} of ${count.toLocaleString()}`,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
